import type { ComponentType } from 'react'
import { AdType } from './advertisement'

export enum TableFilterProperty {
  Device = 'device',
  AdType = 'adType',
  KeywordType = 'keywordType',
  Domains = 'domains',
  Keyword = 'keyword',
  KeywordID = 'keywordID',
  TagDealer = 'tagDealer',
  TagKeyword = 'tagKeyword',
  Group = 'group',
  Position = 'adPosition',
  Title = 'adTitle',
  Description = 'adDescription',
  Date = 'date',
  // Notifications specific
  NotificationStatus = 'notificationStatus',
  NotificationReappearance = 'notificationReappearance',
  NotificationBrand = 'brand',
  NotificationCreatedAt = 'notificationCreatedAt',
  NotificationRequestSubmittedAt = 'notificationRequestSubmittedAt',
  NotificationNotifiedAt = 'notificationNotifiedAt',
  NotificationUpdatedAt = 'notificationUpdatedAt',
  NotificationAcceptedAt = 'notificationAcceptedAt',
  NotificationRefusedAt = 'notificationRefusedAt',
  NotificationLastView = 'notificationLastView',
  // Companies specific
  CompanyName = 'companyName',
  CompanyType = 'companyType',
  CompanyEnable = 'companyEnable',
  // Users specific
  UserEmail = 'userEmail',
  UserCompany = 'userCompany',
  UserRole = 'userRole',
  Country = 'country',
  Engine = 'engine',
  StopGoAdvertiserType = 'stopGoAdvertiserType',
  Price = 'price',
  MerchantRating = 'merchantRating',
  ShippingFees = 'shippingFees',
  Promotions = 'promotions',
  // Notification template specific
  TemplateLanguage = 'templateLanguage',
  TemplateType = 'templateType',
}

export enum TableFilterType {
  MultiSelectPredefined = 'multi-select-predefined',
  MultiSelectCreatable = 'multi-select-creatable',
  FreeText = 'free-text',
  FreeNumber = 'free-number',
  DateInput = 'date-input',
  SingleSelectPredefined = 'single-select-predefined',
  SingleSelectSearchable = 'single-select-searchable',
}

export interface TableFilterPropertyConfig {
  value: string
  inputType: TableFilterType
  options?: {
    label: string
    value: string
    icon?: ComponentType<{ className?: string }>
  }[]
}

export enum FilterValueOperator {
  Is = 'Is',
  IsNot = 'IsNot',
  Contains = 'Contains',
  NotContains = 'NotContains',
  StartsWith = 'StartsWith',
  EndsWith = 'EndsWith',
  IsEmpty = 'IsEmpty',
  IsNotEmpty = 'IsNotEmpty',
  Equal = '=',
  NotEqual = '≠',
  GreaterThan = '>',
  LessThan = '<',
  GreaterThanOrEqual = '≥',
  LessThanOrEqual = '≤',
  // Date operators
  IsBefore = 'IsBefore',
  IsAfter = 'IsAfter',
  IsOnOrBefore = 'IsOnOrBefore',
  IsOnOrAfter = 'IsOnOrAfter',
  IsBetween = 'IsBetween',
}

enum Device {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

enum KeywordType {
  Brand = 'BRAND',
  Generic = 'GENERIC',
  GenericBrand = 'GENERIC_BRAND',
}

enum CompanyType {
  Prospect = 'prospect',
  Customer = 'customer',
  Agency = 'agency',
}

export enum LogicalOperator {
  And = 'AND',
  Or = 'OR',
}

export interface FilterRow {
  property: TableFilterProperty
  operator?: FilterValueOperator
  values?: string[]
  // Inline group representation when property === Group
  logicalOperator?: LogicalOperator
  rows?: FilterRow[]
}

export interface TableFilter {
  logicalOperator: LogicalOperator
  rows: FilterRow[]
}

export const getOperatorsForProperty = (property: TableFilterProperty): FilterValueOperator[] => {
  switch (property) {
    case TableFilterProperty.NotificationCreatedAt:
    case TableFilterProperty.NotificationRequestSubmittedAt:
    case TableFilterProperty.NotificationNotifiedAt:
    case TableFilterProperty.NotificationUpdatedAt:
    case TableFilterProperty.NotificationAcceptedAt:
    case TableFilterProperty.NotificationRefusedAt:
    case TableFilterProperty.NotificationLastView:
      return [
        FilterValueOperator.IsBetween,
        FilterValueOperator.Is,
        FilterValueOperator.IsBefore,
        FilterValueOperator.IsAfter,
        FilterValueOperator.IsOnOrBefore,
        FilterValueOperator.IsOnOrAfter,
        FilterValueOperator.IsEmpty,
        FilterValueOperator.IsNotEmpty,
      ]
    case TableFilterProperty.Device:
    case TableFilterProperty.AdType:
    case TableFilterProperty.KeywordType:
    case TableFilterProperty.NotificationStatus:
    case TableFilterProperty.NotificationReappearance:
    case TableFilterProperty.NotificationBrand:
    case TableFilterProperty.CompanyType:
    case TableFilterProperty.CompanyName:
      return [FilterValueOperator.Is, FilterValueOperator.Contains]
    case TableFilterProperty.CompanyEnable:
      return [FilterValueOperator.Is]
    case TableFilterProperty.UserEmail:
      return [FilterValueOperator.Is]
    case TableFilterProperty.UserCompany:
      return [FilterValueOperator.Contains]
    case TableFilterProperty.UserRole:
    case TableFilterProperty.Country:
    case TableFilterProperty.Engine:
      return [FilterValueOperator.Is]
    case TableFilterProperty.KeywordID:
      return [FilterValueOperator.Is]
    case TableFilterProperty.Position:
      return [
        FilterValueOperator.Equal,
        FilterValueOperator.NotEqual,
        FilterValueOperator.GreaterThan,
        FilterValueOperator.LessThan,
        FilterValueOperator.GreaterThanOrEqual,
        FilterValueOperator.LessThanOrEqual,
        FilterValueOperator.IsEmpty,
        FilterValueOperator.IsNotEmpty,
      ]
    case TableFilterProperty.Price:
    case TableFilterProperty.MerchantRating:
    case TableFilterProperty.ShippingFees:
    case TableFilterProperty.Promotions:
      return [
        FilterValueOperator.GreaterThan,
        FilterValueOperator.LessThan,
        FilterValueOperator.Equal,
        FilterValueOperator.NotEqual,
        FilterValueOperator.GreaterThanOrEqual,
        FilterValueOperator.LessThanOrEqual,
      ]
    default:
      return [
        FilterValueOperator.Is,
        FilterValueOperator.IsNot,
        FilterValueOperator.Contains,
        FilterValueOperator.NotContains,
        FilterValueOperator.StartsWith,
        FilterValueOperator.EndsWith,
        FilterValueOperator.IsEmpty,
        FilterValueOperator.IsNotEmpty,
      ]
  }
}

export const getPredefinedValuesForProperty = (property: TableFilterProperty): string[] => {
  switch (property) {
    case TableFilterProperty.Device:
      return Object.values(Device)
    case TableFilterProperty.AdType:
      return Object.values(AdType)
    case TableFilterProperty.KeywordType:
      return Object.values(KeywordType)
    case TableFilterProperty.CompanyType:
      return Object.values(CompanyType)
    default:
      return []
  }
}

export interface SortConfig {
  by: string
  direction: SortDirection
}

export enum SortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

// Reusable hierarchical option type for table filter options (and related UIs)
export interface HierarchicalOption {
  label: string
  value: string
  children?: HierarchicalOption[]
}

export const handleDuplicateNestedFilter = (
  tableFilter: TableFilter,
  groupIndex: number,
  rowIndex: number,
): TableFilter => {
  const groupRow = tableFilter.rows[groupIndex]
  const duplicate = {
    property: groupRow?.rows?.[rowIndex]?.property || TableFilterProperty.Device,
    operator: groupRow?.rows?.[rowIndex]?.operator || FilterValueOperator.Is,
    values: groupRow?.rows?.[rowIndex]?.values || [],
    logicalOperator: groupRow?.rows?.[rowIndex]?.logicalOperator,
    rows: groupRow?.rows?.[rowIndex]?.rows,
  }
  const newRows = [...tableFilter.rows]
  if (newRows[groupIndex]?.property === TableFilterProperty.Group) {
    newRows[groupIndex].rows?.push(duplicate as FilterRow)
  }
  return { ...tableFilter, rows: newRows }
}

export const handleNestedLogicalOperatorChange = (
  tableFilter: TableFilter,
  groupIndex: number,
  operator: LogicalOperator,
): TableFilter => {
  const updatedRows = [...tableFilter.rows]
  const target = updatedRows[groupIndex]
  if (target?.property === TableFilterProperty.Group) {
    const clonedChildren = target.rows ? [...target.rows] : []
    updatedRows[groupIndex] = {
      ...target,
      logicalOperator: operator,
      rows: clonedChildren,
    }
  }
  return { ...tableFilter, rows: updatedRows }
}

export const handleNestedRowChange = (
  tableFilter: TableFilter,
  groupIndex: number,
  rowIndex: number,
  row: FilterRow,
): TableFilter => {
  const updatedRows = [...tableFilter.rows]
  if (
    updatedRows[groupIndex]?.property === TableFilterProperty.Group &&
    updatedRows[groupIndex].rows
  ) {
    updatedRows[groupIndex].rows![rowIndex] = row
  }
  return { ...tableFilter, rows: updatedRows }
}

export const handleAddNestedFilter = (tableFilter: TableFilter, index: number): TableFilter => {
  const updatedRows = [...tableFilter.rows]
  if (updatedRows[index]?.property === TableFilterProperty.Group) {
    updatedRows[index].rows?.push({
      property: TableFilterProperty.Device,
      operator: FilterValueOperator.Is,
      values: [],
    })
  }
  return { ...tableFilter, rows: updatedRows }
}

export const handleRemoveNestedFilter = (
  tableFilter: TableFilter,
  groupIndex: number,
  rowIndex: number,
): TableFilter => {
  const updatedRows = [...tableFilter.rows]
  if (updatedRows[groupIndex]?.property === TableFilterProperty.Group) {
    updatedRows[groupIndex].rows?.splice(rowIndex, 1)
  }
  return { ...tableFilter, rows: updatedRows }
}

export const handleDuplicateRow = (tableFilter: TableFilter, index: number): TableFilter => {
  const duplicate = { ...tableFilter.rows[index] }
  const newRows = [
    ...tableFilter.rows.slice(0, index + 1),
    duplicate,
    ...tableFilter.rows.slice(index + 1),
  ]
  return { ...tableFilter, rows: newRows }
}

export const handleLogicalOperatorChange = (
  tableFilter: TableFilter,
  operator: LogicalOperator,
): TableFilter => {
  return {
    ...tableFilter,
    logicalOperator: operator,
  }
}

export const handleAddFilterGroup = (tableFilter: TableFilter): TableFilter => {
  return {
    ...tableFilter,
    rows: [
      ...tableFilter.rows,
      {
        property: TableFilterProperty.Group,
        logicalOperator: LogicalOperator.And,
        rows: [
          {
            property: TableFilterProperty.Device,
            operator: FilterValueOperator.Is,
            values: [],
          },
        ],
      },
    ],
  }
}

export const handleAddEmptyFilterGroup = (tableFilter: TableFilter): TableFilter => {
  return {
    ...tableFilter,
    rows: [
      ...tableFilter.rows,
      {
        property: TableFilterProperty.Group,
        logicalOperator: LogicalOperator.And,
        rows: [],
      },
    ],
  }
}

export const removeEmptyValueFilters = (tableFilter: TableFilter): TableFilter => {
  const cloneRow = (r: FilterRow): FilterRow => ({
    property: r.property,
    operator: r.operator,
    values: r.values ? [...r.values] : undefined,
    logicalOperator: r.logicalOperator,
    rows: r.rows ? r.rows.map(cloneRow) : undefined,
  })

  const filtered = tableFilter.rows
    .filter((row) => !!row)
    .map((row) => {
      const current = cloneRow(row)
      if (current.property === TableFilterProperty.Group) {
        const children = current.rows ?? []
        const keptChildren = children.filter((groupRow) => {
          if (groupRow.property === TableFilterProperty.Group) {
            return (groupRow.rows?.length ?? 0) > 0
          }
          return (
            (groupRow.values && groupRow.values.length > 0) ||
            groupRow.operator === FilterValueOperator.IsEmpty ||
            groupRow.operator === FilterValueOperator.IsNotEmpty
          )
        })
        const nextGroup: FilterRow = { ...current, rows: keptChildren }
        return (nextGroup.rows?.length ?? 0) > 0 ? nextGroup : null
      }
      const hasValidValues =
        (current.values && current.values.length > 0) ||
        current.operator === FilterValueOperator.IsEmpty ||
        current.operator === FilterValueOperator.IsNotEmpty
      return hasValidValues ? current : null
    })
    .filter((row): row is FilterRow => row !== null)

  return { ...tableFilter, rows: filtered }
}

// Filters injected from the global brand/country/engine selection; the user cannot remove them
export const NON_REMOVABLE_FILTER_PROPERTIES: TableFilterProperty[] = [
  TableFilterProperty.NotificationBrand,
  TableFilterProperty.Country,
  TableFilterProperty.Engine,
]

export const EMPTY_TABLE_FILTER: TableFilter = { logicalOperator: LogicalOperator.And, rows: [] }

// True when at least one user-removable filter has a value (same rule as the filter chips)
export const hasActiveFilters = (tableFilter: TableFilter): boolean =>
  removeEmptyValueFilters(tableFilter).rows.some(
    (row) => !NON_REMOVABLE_FILTER_PROPERTIES.includes(row.property),
  )

export const handleAddFilter = (filters: TableFilter): TableFilter => {
  return {
    ...filters,
    rows: [
      ...filters.rows,
      {
        property: TableFilterProperty.Device,
        operator: FilterValueOperator.Is,
        values: [],
      },
    ],
  }
}

export const handleRemoveFilter = (tableFilter: TableFilter, index: number): TableFilter => {
  return {
    ...tableFilter,
    rows: tableFilter.rows.filter((_, i) => i !== index),
  }
}

export const handleRowChange = (
  tableFilter: TableFilter,
  index: number,
  row: FilterRow,
): TableFilter => {
  const updatedRows = [...tableFilter.rows]
  updatedRows[index] = row
  return { ...tableFilter, rows: updatedRows }
}

export const handleConvertFilterToGroup = (
  tableFilter: TableFilter,
  index: number,
): TableFilter => {
  const target = tableFilter.rows[index]
  if (!target || target.property === TableFilterProperty.Group) {
    return tableFilter
  }

  const groupRow: FilterRow = {
    property: TableFilterProperty.Group,
    logicalOperator: LogicalOperator.And,
    rows: [
      {
        property: target.property,
        operator: target.operator,
        values: target.values,
      },
    ],
  }

  const updatedRows = [...tableFilter.rows]
  updatedRows[index] = groupRow
  return { ...tableFilter, rows: updatedRows }
}
